import type { McqContent, ScriptElement } from '../../schema';
import type { QuizFeedback } from '../../engine/quiz-types';

export interface McqInteractive {
  feedback: QuizFeedback | null;
  disabled: boolean;
  onAnswer: (optionIndex: number) => void;
}

/**
 * MCQ rendered on the board. Two modes:
 * - static (no `quiz` prop): the question shown while it's being read aloud.
 * - interactive (`quiz` provided): the clickable answer surface during
 *   `quiz_waiting`, with correct/wrong feedback. This replaces the old bottom
 *   QuizOverlay popup so the quiz only ever appears once — on the board.
 */
/**
 * Backends sometimes bake the option lines (e.g. "(a) CH₃CHO") right into the
 * `question` string. Since we also render every option as its own card, that
 * would show each option twice. Strip any question line that just restates an
 * option, keeping only the actual prompt.
 */
function questionPrompt(question: string, options: string[]): string {
  if (!question) return '';
  const norm = (s: string) => s.toLowerCase().replace(/\s+/g, '').replace(/[•\-–—.]/g, '');
  const optSet = new Set(options.map(norm).filter(Boolean));
  const kept = question
    .split(/\r\n|\r|\n/)
    .filter((line) => {
      const t = line.trim();
      if (!t) return false;
      // Drop a leading enumerator like "(a)" / "a)" / "a." / "1." before matching.
      const body = t.replace(/^\(?\s*[a-dA-D0-9]\s*\)?[.)]?\s*/, '');
      return !optSet.has(norm(body)) && !optSet.has(norm(t));
    });
  return kept.join('\n').trim();
}

export function McqElement({ element, quiz }: { element: ScriptElement; quiz?: McqInteractive }) {
  const c = element.content as McqContent | undefined;
  if (!c) return null;
  const options = Array.isArray(c.options) ? c.options : [];
  const prompt = questionPrompt(c.question ?? '', options);

  return (
    <div className="w-full" style={{ color: '#2d2a24' }}>
      {prompt && (
        <div
          className="mb-3 whitespace-pre-line font-medium"
          style={{ fontSize: ((element.style?.fontSize as number | undefined) ?? 22) * 0.8 }}
        >
          {prompt}
        </div>
      )}

      <ul className="space-y-1.5">
        {options.map((opt, i) => {
          const optionId = c.optionIds?.[i] ?? String.fromCharCode(97 + i);
          const badge = optionId.toUpperCase();

          // ── Interactive (clickable) ──
          if (quiz) {
            const { feedback } = quiz;
            const isPicked = feedback?.optionIndex === i;
            let cls = 'border-[#d8cdb6] bg-white text-[#3a3128] hover:bg-[#f6f0e2]';
            // Only the option the user just pressed gets feedback styling.
            // Never reveal the correct answer until the user actually picks it —
            // a wrong pick turns red and the quiz keeps going.
            if (isPicked && feedback) {
              cls = feedback.correct
                ? 'border-[#4ecf78]/70 bg-[#4ecf78]/15 text-[#2f7d4e]'
                : 'border-[#e26a5a]/70 bg-[#e26a5a]/12 text-[#c0432f]';
            }
            return (
              <li key={i}>
                <button
                  type="button"
                  onClick={() => quiz.onAnswer(i)}
                  disabled={quiz.disabled}
                  className={`flex w-full items-center gap-2.5 rounded-lg border px-2.5 py-1 text-left shadow-sm transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${cls}`}
                  style={{ fontSize: 18 * 0.8 }}
                >
                  <span className="mcq-badge grid h-5 w-5 shrink-0 place-items-center rounded-full border border-current/40 text-[9.6px] font-medium">
                    {badge}
                  </span>
                  <span className="whitespace-pre">{opt}</span>
                </button>
              </li>
            );
          }

          // ── Static (read-aloud) ──
          return (
            <li
              key={i}
              className="flex items-center gap-2.5 rounded-lg px-2.5 py-1"
              style={{
                border: '1px solid rgba(60, 45, 25, 0.18)',
                background: 'rgba(255, 255, 255, 0.65)',
                fontSize: 18 * 0.8,
              }}
            >
              <span
                className="mcq-badge grid h-5 w-5 shrink-0 place-items-center rounded-full text-[9.6px] font-medium"
                style={{ color: '#ffffff', background: '#e0892f' }}
              >
                {badge}
              </span>
              <span className="whitespace-pre">{opt}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
